import { useCallback } from "react";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { DISCOVERED_TAG_CATEGORIES } from "@/lib/constants/discovered-tag-categories";
import { useFeature } from "@/lib/feature-management";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import { useDynamicFilters } from "@/store/news-feed/filters/useDynamicFilters";
import { useAnalysisSidebarFilterParams } from "@/store/news-feed/params/analysis-params.context";
import NewsFeedFilterSectionWithSearch from "./NewsFeedFilterSectionWithSearch";

/**
 * Container component for dynamic filters (like authors, publishers, topics)
 * Fetches dynamic filter data and renders filter sections with search functionality
 */
const NewsFeedDynamicFiltersContainer = () => {
  // Check feature flag for conditional fetching
  const fetchOnExpand = useFeature("fetch-dynamic-filters-exp", false);

  // feature flag to hide categories that are in the discovered tag categories
  const discoveredTagsHidden = useFeature("discovered-tags-hidden", false);

  // Get expanded state from news feed filters store
  const expandedCategories = useNewsFeedFilters(
    (state) => state.expandedCategories,
  );

  const { toggleFilterSelection, selectedFilters } =
    useAnalysisSidebarFilterParams();

  const shouldFetchCategory = useCallback(
    (category: ShareVoiceSidebarCategory) => {
      if (discoveredTagsHidden && DISCOVERED_TAG_CATEGORIES.has(category)) {
        return false;
      }
      if (!fetchOnExpand) {
        return true;
      }
      if (expandedCategories.has(category)) {
        return true;
      }
      return (selectedFilters.get(category)?.length ?? 0) > 0;
    },
    [fetchOnExpand, expandedCategories, selectedFilters, discoveredTagsHidden],
  );

  // Fetch dynamic filter data for each category
  const {
    items: themes,
    isLoading: themesLoading,
    error: themesError,
  } = useDynamicFilters("THEME", shouldFetchCategory("THEME"));

  const {
    items: people,
    isLoading: peopleLoading,
    error: peopleError,
  } = useDynamicFilters("PEOPLE", shouldFetchCategory("PEOPLE"));

  const {
    items: authors,
    isLoading: authorsLoading,
    error: authorsError,
  } = useDynamicFilters("AUTHOR", shouldFetchCategory("AUTHOR"));

  const {
    items: publications,
    isLoading: publicationsLoading,
    error: publicationsError,
  } = useDynamicFilters("PUBLISHER", shouldFetchCategory("PUBLISHER"));

  const {
    items: topics,
    isLoading: topicsLoading,
    error: topicsError,
  } = useDynamicFilters("TOPIC", shouldFetchCategory("TOPIC"));

  const {
    items: events,
    isLoading: eventsLoading,
    error: eventsError,
  } = useDynamicFilters("EVENT", shouldFetchCategory("EVENT"));

  const {
    items: initiatives,
    isLoading: initiativesLoading,
    error: initiativesError,
  } = useDynamicFilters("INITIATIVE", shouldFetchCategory("INITIATIVE"));

  const setCategoryExpansion = useNewsFeedFilters(
    (state) => state.setCategoryExpansion,
  );

  // Handle filter toggle
  const handleToggleFilter = useCallback(
    (category: ShareVoiceSidebarCategory, item: string) => {
      toggleFilterSelection(category, item);
    },
    [toggleFilterSelection],
  );

  // Filter configuration for data-driven rendering
  const filterConfigs = [
    {
      title: "People",
      category: "PEOPLE" as const,
      filterData: {
        items: people,
        isLoading: peopleLoading,
        error: peopleError,
      },
    },
    {
      title: "Authors",
      category: "AUTHOR" as const,
      filterData: {
        items: authors,
        isLoading: authorsLoading,
        error: authorsError,
      },
    },
    {
      title: "Publications",
      category: "PUBLISHER" as const,
      filterData: {
        items: publications,
        isLoading: publicationsLoading,
        error: publicationsError,
      },
    },
    {
      title: "Topics",
      category: "TOPIC" as const,
      filterData: {
        items: topics,
        isLoading: topicsLoading,
        error: topicsError,
      },
    },
    {
      title: "Events",
      category: "EVENT" as const,
      filterData: {
        items: events,
        isLoading: eventsLoading,
        error: eventsError,
      },
    },
    {
      title: "Initiatives",
      category: "INITIATIVE" as const,
      filterData: {
        items: initiatives,
        isLoading: initiativesLoading,
        error: initiativesError,
      },
    },
    {
      title: "Themes",
      category: "THEME" as const,
      filterData: {
        items: themes,
        isLoading: themesLoading,
        error: themesError,
      },
    },
  ].filter(
    ({ category }) =>
      !discoveredTagsHidden || !DISCOVERED_TAG_CATEGORIES.has(category),
  );

  return (
    <>
      {filterConfigs.map(({ title, category, filterData }) => (
        <NewsFeedFilterSectionWithSearch
          key={category}
          title={title}
          category={category}
          filterData={filterData}
          selectedFilters={selectedFilters}
          onToggleFilter={handleToggleFilter}
          visibleCount={10}
          isExpanded={expandedCategories.has(category)}
          onValueChange={(value) => {
            setCategoryExpansion(category, value === category);
          }}
        />
      ))}
    </>
  );
};

export default NewsFeedDynamicFiltersContainer;
